import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Menu, Bell, Sun, Moon, Globe, ChevronDown, User, LogOut, Building2, Check } from "lucide-react";

export function Header({ onMenu, title }) {
  const { t, user, theme, toggleTheme, language, setLanguage, logout, activeBranch, activeBranchId, setActiveBranchId, isOrgView } = useApp();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);

  const loadNotifs = async () => {
    try { const { data } = await api.get("/notifications"); setNotifs(data); } catch {}
  };
  useEffect(() => { loadNotifs(); }, [activeBranchId]);
  const unread = notifs.filter((n) => !n.read).length;

  const markAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); };

  const branches = user?.branches || [];
  const showBranchSwitcher = branches.length > 1 || user?.is_super_admin;

  return (
    <header className="h-16 border-b border-border bg-card sticky top-0 z-40 flex items-center gap-3 px-4 sm:px-6">
      <button className="lg:hidden" onClick={onMenu} data-testid="header-menu-btn"><Menu className="h-5 w-5" /></button>
      <h2 className="font-heading font-semibold text-lg hidden sm:block truncate">{title}</h2>

      <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
        {/* branch switcher */}
        {showBranchSwitcher && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 max-w-[200px]" data-testid="branch-switcher-btn">
                <Building2 className="h-4 w-4 text-primary shrink-0" />
                <span className="truncate text-xs">{isOrgView ? t("organizationView") : activeBranch?.name || t("selectBranch")}</span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{t("currentBranch")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {user?.is_super_admin && (
                <DropdownMenuItem onClick={() => setActiveBranchId(null)} data-testid="branch-option-org">
                  <Building2 className="h-4 w-4 mr-2" /> {t("organizationView")}
                  {isOrgView && <Check className="h-4 w-4 ml-auto" />}
                </DropdownMenuItem>
              )}
              {branches.map((b) => (
                <DropdownMenuItem key={b.id} onClick={() => setActiveBranchId(b.id)} data-testid={`branch-option-${b.code}`}>
                  <span className="truncate">{b.name}</span>
                  {activeBranchId === b.id && <Check className="h-4 w-4 ml-auto" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="relative h-9 w-9 rounded-md hover:bg-muted flex items-center justify-center transition-colors" data-testid="notifications-btn">
              <Bell className="h-4.5 w-4.5" />
              {unread > 0 && <span className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 rounded-full bg-secondary text-white text-[9px] flex items-center justify-center font-bold">{unread}</span>}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <div className="flex items-center justify-between px-2 py-1.5">
              <DropdownMenuLabel className="p-0">{t("notifications")}</DropdownMenuLabel>
              {unread > 0 && <button className="text-xs text-primary" onClick={markAll} data-testid="notif-mark-all">{t("markAllRead")}</button>}
            </div>
            <DropdownMenuSeparator />
            {notifs.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">{t("noNotifications")}</p>
            ) : notifs.slice(0, 6).map((n) => (
              <div key={n.id} className={cn("px-3 py-2 border-b border-border last:border-0", !n.read && "bg-muted/40")}>
                <div className="flex items-center gap-2">
                  <span className={cn("h-1.5 w-1.5 rounded-full shrink-0",
                    n.type === "warning" ? "bg-[hsl(var(--warning))]" : n.type === "success" ? "bg-[hsl(var(--success))]" : "bg-primary")} />
                  <p className="text-sm font-medium truncate">{n.title}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* language */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="h-9 w-9 rounded-md hover:bg-muted flex items-center justify-center transition-colors" data-testid="language-btn">
              <Globe className="h-4.5 w-4.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setLanguage("en")} data-testid="lang-en">English {language === "en" && <Check className="h-4 w-4 ml-auto" />}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setLanguage("sw")} data-testid="lang-sw">Kiswahili {language === "sw" && <Check className="h-4 w-4 ml-auto" />}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* theme */}
        <button className="h-9 w-9 rounded-md hover:bg-muted flex items-center justify-center transition-colors" onClick={toggleTheme} data-testid="theme-toggle-btn">
          {theme === "dark" ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
        </button>

        {/* user */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 pl-1.5 pr-2 h-9 rounded-md hover:bg-muted transition-colors" data-testid="user-menu-btn">
              <div className="h-7 w-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div className="hidden md:block text-left leading-tight">
                <p className="text-xs font-semibold">{user?.first_name} {user?.last_name}</p>
                <p className="text-[10px] text-muted-foreground">{user?.role_names?.[0]}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <p className="text-sm">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-muted-foreground font-normal">{user?.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
              <User className="h-4 w-4 mr-2" /> {t("myProfile")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="menu-logout">
              <LogOut className="h-4 w-4 mr-2" /> {t("logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
